import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { Point } from 'highcharts';
import Highcharts from 'highcharts/highmaps';
import { every, get, isEmpty, isNil, lowerCase, map, pickBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import CustomTable from 'src/components/Table';
import CompanyTradeDataWhiteBox from 'src/features/CompanyTradeData/common/CompanyTradeDataWhiteBox';
import DataGuard from 'src/features/CompanyTradeData/common/DataGuard';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryCTDTradingAreaList,
  useQueryCTDTradingAreaWorldMap,
} from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy } from 'src/utils/constants/enum';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import {
  getCountryByCountryCode,
  renderNumberOrDefault,
  renderValueNumberTrade,
} from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

const CompanyTradeDataTradingArea = () => {
  const { t } = useTranslation();
  const { setSort, pooSortParams } = useTradeDataSortStore();
  const queryParams = useQueryParams();
  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState(tradeDataUnitByFilter.value);
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });
  const {
    palette: { text },
  } = useTheme();
  const isBuyer = queryParams.is_buyer === 'true';
  const [proportionTablePaging, setProportionTablePaging] = useState({
    offset: 0,
    limit: LIMIT_GET_LIST,
  });
  const unitFieldMapping = {
    bol: {
      field: 'bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value_usd', label: 'USD' },
  };

  const tradingAreaWorldMapRequestPayload = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        start_date: selectedRangeTime.fromTime,
        end_date: selectedRangeTime.toTime,
        keyword: queryParams.keyword,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [queryParams, selectedRangeTime, isBuyer]);

  const tradingAreListRequestPayload = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        start_date: selectedRangeTime.fromTime,
        end_date: selectedRangeTime.toTime,
        keyword: queryParams.keyword,
        sort_by: pooSortParams?.sortBy,
        order: pooSortParams?.sort,
        ...proportionTablePaging,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [queryParams, selectedRangeTime, isBuyer, pooSortParams, proportionTablePaging]);

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const tradingAreaWorldMapResp = useQueryCTDTradingAreaWorldMap(tradingAreaWorldMapRequestPayload);
  const tradingAreaListResp = useQueryCTDTradingAreaList(tradingAreListRequestPayload);

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (
      tradingAreaListResp.isFetching ||
      tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)
    )
      return;
    const isEmptyData =
      isEmpty(tradingAreaListResp.data?.data) ||
      every(tradingAreaListResp.data?.data, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [tradingAreaListResp]);

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: tradingAreaListResp.data,
    featureInfo: {
      featureId: LIST_FEATURES.COMPANY_PROFILE_TRADING_AREA,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });
  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;

  const worldMapChart = useMemo(() => {
    const data = map(tradingAreaWorldMapResp?.data?.data, (item) => {
      return {
        'hc-key': lowerCase(item?.country_code),
        value: get(item, chartFieldNameByUnitFilter, 0),
        percentage: get(item, `percentage_${chartFieldNameByUnitFilter}`, 0),
      };
    }) as Highcharts.SeriesMapDataOptions[];
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !shouldShowBillableDataLoading &&
          !tradingAreaWorldMapResp.isFetching &&
          isEmpty(tradingAreaWorldMapResp?.data?.data)
        }
        emptyTitle={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        emptyContent={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      >
        <WorldMapChart
          tooltipFormatter={function () {
            const point: Point = this.point;
            const country_code = get(point.options, 'hc-key', '');
            const flag = getEmojiFlag(country_code);
            const countryName = getCountryByCountryCode(country_code).label;
            const value = renderValueNumberTrade(point.options.value);
            const percentage = get(point.options, 'percentage', 0);
            return `<div style="color: #fff; min-width: 220px; line-height: 150%; font-size: 12px; display: flex; flex-direction: column; gap: 4px; font-weight: 600">
              <span style="color: inherit; font-size: 14px">${flag} ${countryName}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">${chartLabelByUnitFilter}:</span> ${value}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">Percentage:</span> ${percentage}%</span>
              <span style="color: inherit">
             </div>`;
          }}
          data={data}
          isLoading={shouldShowBillableDataLoading || tradingAreaWorldMapResp.isFetching}
          height={580}
          type={WorldMapOptions.customer}
        />
      </DataGuard>
    );
  }, [JSON.stringify(tradingAreaWorldMapResp), unitFilter]);

  const proportionTableConfigColumn: ConfigCell<ICTDAggregatedMapByBOL>[] = [
    {
      disablePadding: false,
      disableSort: true,
      id: 'Country',
      label: 'Country',
      cell: (data: ICTDAggregatedMapByBOL) => {
        return (
          <Stack gap={1} direction="row">
            <FlagCountry code={data.country_code} /> {getCountryByCountryCode(data.country_code).label}
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ICTDAggregatedMapByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ICTDAggregatedMapByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ICTDAggregatedMapByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={0.5} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ICTDAggregatedMapByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <CompanyTradeDataWhiteBox>
        <FilterChart
          selectedUnitBy={unitFilter}
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as any);
          }}
          selectedRangeTime={selectedRangeTime}
          onSelectTimeRange={setSelectedRangeTime}
          title={'Trade Proportion by Country'}
        />
        {worldMapChart}
      </CompanyTradeDataWhiteBox>
      <CompanyTradeDataWhiteBox>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <ExportDataTable
            fileName="ctd-trading-area"
            featureId={LIST_FEATURES.COMPANY_PROFILE_TRADING_AREA}
            endpoint={get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.CTD_TRADING_AREA)}
            paramsExport={tradingAreListRequestPayload}
            totalRecordsAvailable={tradingAreaListResp.data?.total_records || 0}
          />
        </Stack>
        <CustomTable
          handleSort={(data) => {
            setSort('pooSortParams', data);
          }}
          onChangePage={(value) => setProportionTablePaging((prev) => ({ ...prev, offset: value }))}
          onChangePageSize={(value) => setProportionTablePaging((prev) => ({ ...prev, limit: value, offset: 0 }))}
          loading={shouldShowBillableDataLoading || tradingAreaListResp.isFetching}
          page={proportionTablePaging.offset}
          pageSize={proportionTablePaging.limit}
          totalRecords={tradingAreaListResp.data?.total_records}
          dataKey={'bol'}
          data={tradingAreaListResp.data?.data ?? []}
          configColumn={proportionTableConfigColumn}
        />
      </CompanyTradeDataWhiteBox>
    </>
  );
};

export default CompanyTradeDataTradingArea;
